import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import authService from "../Services/authService";
import analytics from "../Services/analyticsService";
import { RUN_LAUNCH_SEEN_KEY as SEEN_KEY, shouldShowRunLaunch } from "../Services/runLaunch";

const readStore = (fn) => {
  try {
    return fn(window.localStorage);
  } catch {
    return null;
  }
};

function MiniRow() {
  const cards = [
    ["Banana", false],
    ["Bear", true],
    ["Brazil", false],
  ];
  return (
    <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-3">
      <div className="flex items-center justify-center gap-2 text-sm font-bold text-gray-700">
        <span
          className="text-2xl leading-none text-red-600"
          style={{ fontFamily: '"Baloo 2", sans-serif', fontWeight: 800 }}
        >
          B
        </span>
        <span className="uppercase tracking-wide">Animals</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {cards.map(([word, right]) => (
          <div
            key={word}
            className={`rounded-lg bg-white py-1.5 text-center text-sm font-bold shadow-sm border-2 ${
              right
                ? "border-green-500 text-gray-900"
                : "border-transparent text-gray-400 line-through decoration-red-400"
            }`}
          >
            {word}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RunLaunchModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let timer;
    let checked = false;
    const unsub = onAuthStateChanged(authService.auth, (user) => {
      if (!user || checked) return;
      checked = true;
      const show = shouldShowRunLaunch({
        now: Date.now(),
        accountCreatedAt: Date.parse(user.metadata?.creationTime || ""),
        seen: readStore((s) => s.getItem(SEEN_KEY)),
        playedRun: readStore((s) =>
          Object.keys(s).some((k) => k.startsWith("nameit.run.")),
        ),
      });
      if (!show) return;
      // Let the home screen settle before anything slides over it.
      timer = setTimeout(() => {
        readStore((s) => s.setItem(SEEN_KEY, "1"));
        analytics.track("run_announcement_viewed", {});
        setOpen(true);
      }, 1200);
    });
    return () => {
      unsub();
      clearTimeout(timer);
    };
  }, []);

  if (!open) return null;

  const tryIt = () => {
    analytics.track("run_entry_tapped", { source: "announcement" });
    navigate("/run", { state: { from: "announcement" } });
  };

  return (
    <>
      <style>{`.run-launch-pop{animation:runLaunchUp .35s cubic-bezier(.2,1,.3,1) both}@media (min-width:640px){.run-launch-pop{animation-name:runLaunchPop}}
@keyframes runLaunchUp{from{transform:translateY(100%)}to{transform:none}}
@keyframes runLaunchPop{from{opacity:0;transform:scale(.9) translateY(12px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.run-launch-pop{animation:none}}`}</style>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:px-4"
        onClick={() => setOpen(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="run-launch-title"
          className="run-launch-pop w-full max-w-lg sm:max-w-sm rounded-t-3xl sm:rounded-3xl bg-white px-6 pt-3 sm:pt-6 pb-[max(20px,env(safe-area-inset-bottom))] shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-1 bg-gray-300 rounded-full mb-4 sm:hidden" />
          <div className="relative">
            <img src="/run/icon-192.png" alt="" className="w-20 h-20 rounded-[22px] shadow-lg" />
            <span className="absolute -top-2 -right-4 rotate-6 text-[11px] font-bold uppercase tracking-widest text-white bg-red-600 rounded-full px-2 py-0.5 shadow">
              New
            </span>
          </div>
            <h2
              id="run-launch-title"
              className="mt-3 text-2xl leading-tight text-gray-900"
              style={{ fontFamily: '"Baloo 2", sans-serif', fontWeight: 800 }}
            >
              A new way to play NameIt
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Meet <b>NameIt Run</b>. Dash through the word that fits the letter
              and category, and jump everything else.
            </p>
          </div>

          <MiniRow />

          <button
            onClick={tryIt}
            className="mt-5 w-full rounded-2xl bg-red-600 py-3 text-lg font-bold text-white shadow-[0_4px_0_#991b1b] active:translate-y-0.5 active:shadow-[0_2px_0_#991b1b] transition"
          >
            Try NameIt Run
          </button>
          <button
            onClick={() => setOpen(false)}
            className="mt-2 w-full py-2 text-sm font-semibold text-gray-500"
          >
            Maybe later
          </button>
        </div>
      </div>
    </>
  );
}
